"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { usePrivy } from "@privy-io/react-auth";

import PortalNavBar from "../components/PortalNavBar";
import { backendFetch, getCurrentUser } from "../lib/backend";

type EarningsRecord = {
  id: number;
  amount: number;
  created_at?: string;
  createdAt?: string;
};

const MAX_RETRIES = 3;

function formatDate(dateValue?: string) {
  if (!dateValue) return "N/A";

  const parsedDate = new Date(dateValue);

  if (Number.isNaN(parsedDate.getTime())) {
    return "N/A";
  }

  return parsedDate.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export const dynamic = "force-dynamic";

export default function EarningsHistoryPage() {
  const [earningsHistory, setEarningsHistory] = useState<EarningsRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const { ready, authenticated, login, getAccessToken } = usePrivy();

  const navLinks = [
    { href: "/dashboard", label: "Dashboard" },
    { href: "/account", label: "Account" },
    { href: "/accounts-breakdown", label: "Accounts Breakdown" },
    { href: "/agpsira-checkout-app", label: "AGPSIRA Checkout App" },
    { href: "/revenue-history", label: "Revenue History" },
    { href: "/earnings-history", label: "Earnings History", active: true },
    { href: "/sales-history", label: "Sales History" },
    { href: "/distribution-controls", label: "Distribution Controls" },
  ];

  useEffect(() => {
    let isMounted = true;

    if (!ready || !authenticated) {
      return () => {
        isMounted = false;
      };
    }

    async function loadData(attempt = 0) {
      try {
        setLoading(true);
        setError(null);

        const accessToken = await getAccessToken();

        if (!accessToken) {
          throw new Error("Access token unavailable.");
        }

        const account = await getCurrentUser(accessToken);

        let records: EarningsRecord[] = [];

        try {
          const response = await backendFetch(
            `/api/customer/${account.user.walletAddress}/earning-history`,
            accessToken
          );
          if (response.ok) {
            const payload = await response.json();
            records = Array.isArray(payload?.history) ? payload.history : [];
          }
        } catch {
          // Fallback to participant access if customer route is unavailable
        }

        if (records.length === 0) {
          try {
            const membershipResponse = await backendFetch(
              "/api/participant-access/me",
              accessToken
            );
            if (membershipResponse.ok) {
              const membershipData = await membershipResponse.json();
              const primaryId = membershipData.memberships?.[0]?.agreementId;
              if (primaryId !== undefined) {
                const reportRes = await backendFetch(
                  `/api/participant-access/${primaryId}/revenue`,
                  accessToken
                );
                if (reportRes.ok) {
                  const reportData = await reportRes.json();
                  records = Array.isArray(reportData?.earnings)
                    ? reportData.earnings.map((e: any) => ({
                        id: e.id ?? 1,
                        amount: Number(e.amount ?? e.total_earned ?? 0),
                        created_at: e.created_at ?? e.date,
                      }))
                    : [];
                }
              }
            }
          } catch {
            // Ignore fallback error
          }
        }

        if (!isMounted) return;

        setEarningsHistory(records);
        setLoading(false);
      } catch (fetchError) {
        if (!isMounted) return;

        const message =
          fetchError instanceof Error
            ? fetchError.message
            : "Unable to load earnings history.";

        // If auth error (401/403) or token unverified, do not endlessly retry
        if (
          message.includes("401") ||
          message.includes("403") ||
          message.includes("unverified") ||
          attempt >= MAX_RETRIES
        ) {
          setError(message);
          setLoading(false);
          return;
        }

        const nextAttempt = attempt + 1;
        setRetryCount(nextAttempt);

        setTimeout(() => {
          void loadData(nextAttempt);
        }, 1000 * nextAttempt);
      }
    }

    void loadData();

    return () => {
      isMounted = false;
    };
  }, [authenticated, getAccessToken, ready]);

  const totalEarnings = earningsHistory.reduce(
    (sum, record) => sum + Number(record.amount || 0),
    0
  );

  const handleRetry = () => {
    setError(null);
    setRetryCount(0);
    setLoading(true);

    window.location.reload();
  };

  if (!ready || !authenticated) {
    return (
      <main className="min-h-screen bg-slate-950 px-4 py-12 text-white md:px-8 md:py-16">
        <section className="mx-auto max-w-xl rounded-3xl border border-slate-800 bg-slate-900 p-8 text-center">
          <div className="mb-2 inline-block rounded-full bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-300">
            PSE Participant Portal
          </div>
          <h1 className="mt-2 text-3xl font-bold">Earnings History</h1>
          <p className="mt-3 text-slate-300">
            Sign in with your registered email or wallet to review your historical Share-In-Revenues (SiR) earnings.
          </p>
          <button
            type="button"
            onClick={login}
            className="mt-8 w-full rounded-2xl bg-blue-600 py-4 text-base font-semibold text-white hover:bg-blue-500 shadow-lg shadow-blue-600/30"
          >
            Sign In to View Earnings
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-slate-950 text-white">
      <section className="mx-auto max-w-7xl px-4 py-12 md:px-8 md:py-16">
        <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="mb-2 inline-block rounded-full bg-blue-500/20 px-3 py-1 text-xs font-semibold text-blue-300">
              PSE / Client Portal
            </div>
            <h1 className="text-4xl font-bold md:text-6xl">Earnings History</h1>
            <p className="mt-2 text-lg text-slate-300">
              Review your historical Share-In-Revenues (SiR) earnings.
            </p>
          </div>

          <Link
            href="/dashboard"
            className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-medium text-slate-200 hover:border-slate-500"
          >
            Back to Dashboard
          </Link>
        </div>

        <PortalNavBar links={navLinks} portalTitle="Participant Navigation" />

        {error && (
          <div className="mb-8 rounded-3xl border border-red-500/30 bg-slate-900 p-8 text-slate-200">
            <h2 className="mb-3 text-2xl font-semibold text-red-300">Unable to load earnings</h2>
            <p className="mb-4 text-sm text-slate-300">{error}</p>
            <div className="flex flex-wrap gap-3">
              {(error.includes("401") || error.includes("unverified") || error.includes("token")) ? (
                <button
                  type="button"
                  onClick={login}
                  className="rounded-full bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-500"
                >
                  Sign In Again
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleRetry}
                className="rounded-full bg-red-500 px-5 py-2.5 text-sm font-medium text-white transition hover:bg-red-400"
              >
                Retry request
              </button>
            </div>
          </div>
        )}

        <div className="mb-12">
          <div className="max-w-md rounded-3xl border border-slate-800 bg-slate-900 p-8">
            <p className="mb-2 text-slate-400">Lifetime Earnings</p>
            <h2 className="text-4xl font-bold text-green-400">${totalEarnings.toFixed(2)}</h2>
          </div>
        </div>

        <div className="overflow-hidden rounded-3xl border border-slate-800 bg-slate-900">
          <div className="hidden bg-slate-800 font-semibold md:grid md:grid-cols-2">
            <div className="p-5">Date</div>
            <div className="p-5">Earnings</div>
          </div>

          {loading ? (
            <div className="p-10 text-center text-slate-400">Loading earnings...</div>
          ) : earningsHistory.length === 0 ? (
            <div className="p-10 text-center text-slate-400">No earnings history found.</div>
          ) : (
            earningsHistory.map((record) => (
              <div key={record.id} className="border-t border-slate-800 p-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div>{formatDate(record.created_at ?? record.createdAt)}</div>
                  <div>
                    <span className="font-semibold text-green-400">
                      ${Number(record.amount || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </section>
    </main>
  );
}